import { Color3, Matrix, Mesh, MeshBuilder, SceneLoader, StandardMaterial, Vector3, type Scene } from '@babylonjs/core';
import '@babylonjs/loaders/glTF';
import { CARDINAL_STEPS, createLogger, SeededRng, resolveTileCenter, type MapBounds, type ThemeFile, type TileMapMetadata, type WorldSpace } from '@sorrem/shared';
import { resolveSceneYaw, worldSpaceToScenePosition } from './world-to-scene-position';
import { SceneLoadOnceCaches } from './load-once-cache';

const logger = createLogger('engine:map-meshes-with-models');

// Loading a wall/corner GLB involves a network fetch plus one-time work
// (recentering, tile-footprint scaling - see loadMeshModel) that's identical
// every time the same file is loaded, so the resulting base mesh is cached
// per scene, keyed by its file path and tile, and reused across every map
// load in that scene - without this, switching maps (or reloading the same
// map) re-fetched and re-processed every GLB from scratch every time.
// Concurrent loads of one key share one fetch. The cache goes with its
// scene, so a later scene never gets a disposed one's meshes.
const wallBaseMeshes = new SceneLoadOnceCaches<string, Mesh>((mesh) => mesh.dispose());

// Tile catalogs by path. A map is built a chunk at a time (see MapMeshChunks),
// so without this every chunk would fetch the catalog again.
const tileCatalogLoads = new Map<string, Promise<TileCatalog>>();

// One red wireframe material per scene for every fallback cube: a material
// per cube was never disposed with the cube, so each rebuild leaked them.
const fallbackMaterials = new WeakMap<Scene, StandardMaterial>();

function getFallbackMaterial(scene: Scene): StandardMaterial {
    let material = fallbackMaterials.get(scene);
    if (!material) {
        material = new StandardMaterial('fallback-mat', scene);
        material.emissiveColor = new Color3(1, 0, 0);
        material.wireframe = true;
        fallbackMaterials.set(scene, material);
    }
    return material;
}

export interface BuildMapMeshesOptions {
    readonly tileUnitSize: number;
    readonly levelHeight?: number;
    readonly wallHeightTileUnits?: number;
    /** Tiles to cover with the theme's `floorModel`, when it has one. */
    readonly floorTiles?: readonly WorldSpace[];
    /** Builds only the wall models on tiles inside this region; the whole map when left out. */
    readonly region?: MapBounds;
}

export interface MapMeshBuildResult {
    readonly meshes: readonly Mesh[];
    readonly floorMeshes: readonly Mesh[];
    readonly wallMeshes: readonly Mesh[];
}

export interface TileCatalog {
    catalog: string;
    version: string;
    tiles: Record<string, TileDefinition>;
}

export interface TileDefinition {
    file: string;
    meshOrientation?: 'north' | 'east' | 'south' | 'west';
    bounds?: { max: number[] };
    material?: string;
    edgeAlignment?: string;
    /**
     * Uniform scale applied after centering, instead of fitting the model to
     * one tile footprint. `1` keeps a model that is already at game size
     * (for example a pillar narrower than a tile) as authored.
     */
    scale?: number;
}

/** Tile-space offset from a tile's center to each of its corners. */
const CORNER_OFFSET: Readonly<Record<string, { x: number; y: number }>> = {
    northwest: { x: -0.5, y: -0.5 },
    northeast: { x: 0.5, y: -0.5 },
    southeast: { x: 0.5, y: 0.5 },
    southwest: { x: -0.5, y: 0.5 },
};

/**
 * Loads a tile catalog, once per path; a failed load is tried again next time.
 */
export function loadTileCatalog(catalogPath: string): Promise<TileCatalog> {
    let loading = tileCatalogLoads.get(catalogPath);
    if (!loading) {
        loading = fetch(catalogPath).then((response) => {
            if (!response.ok) {
                throw new Error(`Failed to load tile catalog: ${catalogPath}`);
            }
            return response.json() as Promise<TileCatalog>;
        });
        tileCatalogLoads.set(catalogPath, loading);
        loading.catch(() => tileCatalogLoads.delete(catalogPath));
    }
    return loading;
}

/**
 * Load a GLB mesh and cache it.
 * Loads all meshes from the file and returns the first one with geometry.
 */
async function loadMeshModel(
    scene: Scene,
    meshPath: string,
    cacheName: string,
    tileUnitSize: number,
    fit: Pick<TileDefinition, 'scale'> = {},
): Promise<Mesh> {
    try {
        // Split path into rootUrl and filename for SceneLoader
        const lastSlashIndex = meshPath.lastIndexOf('/');
        const rootUrl = meshPath.substring(0, lastSlashIndex + 1);
        const fileName = meshPath.substring(lastSlashIndex + 1);

        // Load all meshes from the file (don't try to extract by name,
        // as the actual mesh names in the GLB files may differ from catalog names)
        const result = await SceneLoader.ImportMeshAsync('', rootUrl, fileName, scene);

        if (!result.meshes || result.meshes.length === 0) {
            throw new Error(`No meshes loaded from ${fileName}`);
        }

        // glTF imports include an empty "__root__" transform node alongside the
        // real geometry mesh(es) - result.meshes[0] is that empty root, not
        // actual geometry, and createInstance() requires a mesh with vertices.
        const geometryMeshes = result.meshes.filter((m): m is Mesh => m instanceof Mesh && m.getTotalVertices() > 0);

        if (geometryMeshes.length === 0) {
            throw new Error(`No mesh with geometry found in ${fileName}`);
        }

        let mesh: Mesh;
        const firstGeometryMesh = geometryMeshes[0];
        if (geometryMeshes.length === 1 && firstGeometryMesh) {
            mesh = firstGeometryMesh;
        } else {
            const merged = Mesh.MergeMeshes(geometryMeshes, true, true, undefined, true, true);
            if (!merged) {
                throw new Error(`Failed to merge geometry meshes in ${fileName}`);
            }
            mesh = merged;
        }

        // Bake the mesh's world transform (inherited from the __root__ parent)
        // into its vertex data, then detach it - createInstance() instances copy
        // the base mesh's local transform, so it must be identity for the
        // per-tile position set later to be correct.
        mesh.computeWorldMatrix(true);
        mesh.bakeTransformIntoVertices(mesh.getWorldMatrix());
        mesh.parent = null;
        mesh.position.setAll(0);
        mesh.rotationQuaternion = null;
        mesh.rotation.setAll(0);
        mesh.scaling.setAll(1);

        // Every piece in this catalog was authored side-by-side in one shared
        // scene (like a sprite sheet) before being exported individually, and the
        // export never recentered each piece's own pivot - the raw vertex data
        // still sits wherever that piece happened to be laid out (e.g.
        // wall-straight-detail's vertices are around X=14-15, not 0). Left as-is,
        // createInstance() + a per-tile `instance.position` would add the tile
        // position ON TOP of that leftover offset, placing every instance many
        // units away from its tile - a different, wrong amount per mesh. Baking
        // out the horizontal bounding-box center (and dropping the vertical
        // bottom to y=0, so the piece sits on the floor) fixes this once here for
        // every future instance of this mesh.
        mesh.refreshBoundingInfo();
        const localBounds = mesh.getBoundingInfo().boundingBox;
        const centerX = (localBounds.minimum.x + localBounds.maximum.x) / 2;
        const centerZ = (localBounds.minimum.z + localBounds.maximum.z) / 2;
        const bottomY = localBounds.minimum.y;
        if (centerX !== 0 || centerZ !== 0 || bottomY !== 0) {
            mesh.bakeTransformIntoVertices(Matrix.Translation(-centerX, -bottomY, -centerZ));
            mesh.refreshBoundingInfo();
        }

        // Catalog models are authored in inconsistent real-world units (e.g.
        // corner pieces are ~4x the footprint of straight wall pieces), so each
        // mesh is independently scaled to fit exactly one tile footprint and that
        // scale is baked into the vertex data - every instance created from this
        // mesh then renders at the correct size with no per-instance scaling.
        const extents = mesh.getBoundingInfo().boundingBox.extendSize;
        const footprint = Math.max(extents.x, extents.z) * 2;
        if (fit.scale !== undefined) {
            mesh.scaling.setAll(fit.scale * tileUnitSize);
            mesh.computeWorldMatrix(true);
            mesh.bakeTransformIntoVertices(mesh.getWorldMatrix());
            mesh.scaling.setAll(1);
            mesh.refreshBoundingInfo();
        } else if (footprint > 0) {
            const scaleFactor = tileUnitSize / footprint;
            mesh.scaling.setAll(scaleFactor);
            mesh.computeWorldMatrix(true);
            mesh.bakeTransformIntoVertices(mesh.getWorldMatrix());
            mesh.scaling.setAll(1);
            mesh.refreshBoundingInfo();
        }

        // Keep the master mesh out of the render at its own (now-origin) transform -
        // only its instances, placed per-tile below, should be visible.
        mesh.isVisible = false;

        mesh.name = cacheName;
        return mesh;
    } catch (error) {
        logger.error(`Failed to load mesh: ${meshPath}`, error);
        throw error;
    }
}

/**
 * Build wall meshes using model-based system with auto-tiling.
 *
 * 1. Loads the theme's wall mesh catalog
 * 2. Pre-loads all required GLB models
 * 3. Walks the tile grid and instances walls based on neighbor config
 * 4. Merges instances by base mesh for optimal draw calls
 */
export async function buildWallMeshesWithModels(
    scene: Scene,
    metadata: TileMapMetadata,
    theme: ThemeFile,
    options: BuildMapMeshesOptions,
): Promise<MapMeshBuildResult> {

    if (!theme.tileCatalog) {
        throw new Error('Theme must include tileCatalog configuration');
    }

    // Load tile catalog
    const catalogPath = theme.tileCatalog;
    const catalog = await loadTileCatalog(catalogPath);

    // Tile paths in catalog are relative to the catalog directory
    const catalogDir = catalogPath.substring(0, catalogPath.lastIndexOf('/') + 1);

    // Load materials from catalog
    const materialsLoader = (await import('./materials-loader')).getMaterialsLoader();
    const materialsByTile = new Map<string, StandardMaterial>();

    for (const [tileName, tileDef] of Object.entries(catalog.tiles)) {
        if (tileDef.material) {
            try {
                const material = await materialsLoader.loadMaterial(tileDef.material, scene);
                if (material) materialsByTile.set(tileName, material);
            } catch (error) {
                logger.warn(`Failed to load material for tile ${tileName}:`, error);
            }
        }
    }

    // Pre-load all tile mesh models (in parallel, not sequentially)
    const meshCache = new Map<string, Mesh>();

    const loadTasks = Object.entries(catalog.tiles).map(async ([tileName, tileDef]) => {
        const meshPath = `${catalogDir}${tileDef.file}`;
        // Create a unique cache key that includes the specific tile name
        const cacheKey = `${meshPath}#${tileName}`;
        try {
            const loadedMesh = await wallBaseMeshes.of(scene).get(cacheKey, () => loadMeshModel(scene, meshPath, tileName, options.tileUnitSize, tileDef));
            meshCache.set(tileName, loadedMesh);
            return { tileName, success: true };
        } catch (error) {
            logger.warn(`Skipping failed mesh: ${tileName}`, error);
            return { tileName, success: false };
        }
    });

    await Promise.all(loadTasks);

    // Walk every authored wall tile position directly - `metadata.wallModelConfigs`
    // (produced by assignWallModelConfigs from the map's own Walls layer) is
    // the sole source of truth for "is this tile a wall," exactly like the map
    // debugger's floor/wall classification grid uses it. Scanning the whole
    // bounds grid and gating on `findGroundGid` (a *floor*-oriented
    // lookup with a cross-layer fallback) used to treat every floor tile as a
    // wall position too, since it happily returns the floor's own gid there.
    const wallMeshes: Mesh[] = [];
    let fallbackCount = 0;

    const region = options.region;
    const inRegion = (x: number, y: number): boolean =>
        !region || (x >= region.minX && y >= region.minY && x < region.maxX && y < region.maxY);
    for (const wallModelConfig of metadata.wallModelConfigs ?? []) {
        const { x: tileX, y: tileY, meshName, rotationDegrees, face, corner } = wallModelConfig;
        if (!inRegion(tileX, tileY)) {
            continue;
        }
        // Profile walls replace the face panels (see buildMapMeshes); pillars stay.
        if (face && theme.walls?.profile) {
            continue;
        }

        // Find the base mesh
        const baseMesh = meshCache.get(meshName);
        if (!baseMesh) {
            logger.warn(`Mesh not found: ${meshName} at (${tileX}, ${tileY}). Creating fallback cube.`);
            // Create fallback cube
            const fallbackCube = MeshBuilder.CreateBox(
                `fallback-cube-${tileX}-${tileY}`,
                {
                    size: Math.max(options.tileUnitSize * 0.8, 0.5),
                },
                scene,
            );
            const pos = worldSpaceToScenePosition(resolveTileCenter(tileX, tileY), options.tileUnitSize, options.levelHeight);
            fallbackCube.position = new Vector3(pos.x, pos.y, pos.z);
            // A debug material makes the fallback clearly visible.
            fallbackCube.material = getFallbackMaterial(scene);

            wallMeshes.push(fallbackCube);
            fallbackCount++;
            continue;
        }

        // InstancedMesh has no per-instance material in Babylon (setting one is a
        // silent no-op, logged as "Setting material on an instanced mesh has no
        // effect") - every instance always renders with its source mesh's
        // material, so the catalog material is applied once to the shared base
        // mesh instead, the first time it's used.
        if (!baseMesh.material) {
            const material = materialsByTile.get(meshName);
            if (material) {
                baseMesh.material = material;
            }
        }

        // Create an instance
        const instance = baseMesh.createInstance(`wall-${tileX}-${tileY}`);

        const pos = worldSpaceToScenePosition(resolveTileCenter(tileX, tileY), options.tileUnitSize, options.levelHeight);
        let posX = pos.x;
        let posZ = pos.z;

        if (face) {
            // A panel on one side of the wall tile, its front flush with the tile
            // edge and facing the floor beyond. `rotationDegrees` is the compass
            // direction it faces; the model's front is its local +Z.
            const step = CARDINAL_STEPS[face];
            const depth = baseMesh.getBoundingInfo().boundingBox.extendSize.z * 2;
            const inset = 0.5 * options.tileUnitSize - depth / 2;
            // Tile x maps to scene z and tile y to scene x (see worldSpaceToScenePosition).
            posX += step.y * inset;
            posZ += step.x * inset;
            instance.rotationQuaternion = null;
            instance.rotation.y = resolveSceneYaw(rotationDegrees);
        } else if (corner) {
            const offset = CORNER_OFFSET[corner] ?? { x: 0, y: 0 };
            posX += offset.y * options.tileUnitSize;
            posZ += offset.x * options.tileUnitSize;
        } else if (rotationDegrees !== 0) {
            instance.rotationQuaternion = null;
            instance.rotation.y = (rotationDegrees * Math.PI) / 180;
        }

        // TODO: back-edge alignment (shift a piece by half its catalog footprint
        // toward its back edge, per rotation) was tried and disabled; it needs a
        // rework before it can return.

        instance.position = new Vector3(posX, pos.y, posZ);

        wallMeshes.push(instance as unknown as Mesh);
    }

    if (fallbackCount > 0) {
        logger.warn(`Created ${fallbackCount} fallback wall cubes`);
    }

    // Instances are already optimized for rendering (shared geometry with individual
    // transforms), so we don't merge them. Babylon handles instance rendering efficiently
    // in a single draw call per base mesh type without explicit merging.
    const mergedWalls: Mesh[] = wallMeshes;

    const floors = theme.floorModel ? buildModelFloor(meshCache.get(theme.floorModel), options) : [];

    return {
        meshes: [...mergedWalls, ...floors],
        floorMeshes: floors,
        wallMeshes: mergedWalls,
    };
}

/** Floor model chunk size, in tiles per side. */
const FLOOR_CHUNK_TILES = 8;

/**
 * Covers `options.floorTiles` with thin instances of the floor model, its
 * top at ground level. The model is ~2k vertices, so thin instances keep a
 * chunk of floor at one draw call; the floor is split into chunks because a
 * thin-instance mesh is culled as a whole, and a large crucible's whole
 * floor would otherwise draw every frame. Each tile gets a quarter turn
 * picked by a hash of its position, so the slabs do not read as a repeating
 * grid. Not pickable: thin instances are not picked per tile, so callers
 * keep a flat floor for picking (see `buildMapMeshes`).
 */
function buildModelFloor(baseMesh: Mesh | undefined, options: BuildMapMeshesOptions): Mesh[] {
    const tiles = options.floorTiles ?? [];
    if (!baseMesh) {
        if (tiles.length > 0) {
            logger.warn('Theme floorModel is not in the tile catalog; using flat floor tiles');
        }
        return [];
    }
    const chunks = new Map<string, WorldSpace[]>();
    for (const tile of tiles) {
        const key = `${Math.floor(tile.x / FLOOR_CHUNK_TILES)},${Math.floor(tile.y / FLOOR_CHUNK_TILES)}`;
        const chunk = chunks.get(key);
        if (chunk) {
            chunk.push(tile);
        } else {
            chunks.set(key, [tile]);
        }
    }
    const height = baseMesh.getBoundingInfo().boundingBox.extendSize.y * 2;
    const floors: Mesh[] = [];
    for (const [key, chunkTiles] of chunks) {
        const floor = baseMesh.clone(`floor-models-${key}`, null, true);
        floor.isVisible = true;
        floor.isPickable = false;
        const matrices = new Float32Array(chunkTiles.length * 16);
        chunkTiles.forEach((tile, index) => {
            const pos = worldSpaceToScenePosition(resolveTileCenter(tile.x, tile.y), options.tileUnitSize, options.levelHeight);
            const turns = SeededRng.hash32(`${tile.x},${tile.y}`) % 4;
            Matrix.RotationY((turns * Math.PI) / 2)
                .multiply(Matrix.Translation(pos.x, pos.y - height, pos.z))
                .copyToArray(matrices, index * 16);
        });
        floor.thinInstanceSetBuffer('matrix', matrices, 16, true);
        floors.push(floor);
    }
    return floors;
}
